/**
 * REST client for cabinet-level profile file import (inbox → preview → confirm → job).
 * Endpoints: accounting-hub/v1/cabinet/profile/...
 */

import { getBootstrapData } from '@tpct/admin-ui';

export type InboxArtifactPublic = {
  artifact_id: number;
  artifact_type: string;
  status: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  file_sha256: string;
  uploaded_at: string;
  mapped_at: string | null;
  retention_until: string | null;
  mapped_company_id: number | null;
};

export type MappingPreviewEntry = {
  artifact_id: number;
  mapping_state: string;
  reason?: string;
  target_company_id?: number | null;
  entity_keys?: Array<{ type: string; value: string }>;
  label_candidate?: string | null;
  warnings?: string[];
};

export type ConfirmMappingResult = {
  artifact_id: number;
  outcome: string;
  mapped_company_id?: number | null;
  company_created?: boolean;
  error_code?: string | null;
  error_message?: string | null;
  http_status?: number | null;
};

export type FileExtractionJobPoll = {
  job_id: number;
  status: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  total_items: number;
  done_items: number;
  failed_items: number;
  error: { code?: string; message?: string } | null;
  items: Array<{
    id: number;
    item_type: string;
    inbox_artifact_id: number | null;
    target_company_id: number | null;
    company_artifact_id: number | null;
    status: string;
    attempts: number;
    import_session_id: number | null;
    error: { code?: string; message?: string } | null;
  }>;
};

const parseWpRestMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as {
      message?: string;
      code?: string;
    };
    if (typeof payload.message === 'string' && payload.message.length > 0) {
      return payload.message;
    }
    if (typeof payload.code === 'string' && payload.code.length > 0) {
      return payload.code;
    }
  } catch {
    // ignore
  }
  return `HTTP ${response.status}`;
};

export class ArtifactTypeRequiredError extends Error {
  readonly ambiguousTypes: string[];

  constructor(message: string, ambiguousTypes: string[] = []) {
    super(message);
    this.name = 'ArtifactTypeRequiredError';
    this.ambiguousTypes = ambiguousTypes;
  }
}

export const uploadInboxArtifact = async (
  bootstrapKey: string,
  file: File,
  artifactType?: string,
): Promise<{ status: number; artifact: InboxArtifactPublic }> => {
  const bootstrap = getBootstrapData(bootstrapKey);
  const formData = new FormData();
  formData.append('file', file);
  if (artifactType !== undefined && artifactType !== '') {
    formData.append('artifact_type', artifactType);
  }

  const response = await fetch(`${bootstrap.restUrl}cabinet/profile/artifacts/inbox/upload`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': bootstrap.nonce,
    },
    body: formData,
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (response.status === 422) {
    const payload = (await response.json()) as {
      code?: string;
      message?: string;
      data?: { status?: number; ambiguous_types?: string[] };
    };
    if (payload.code === 'artifact_type_required') {
      const ambiguous =
        Array.isArray(payload.data?.ambiguous_types) ? payload.data.ambiguous_types : [];
      throw new ArtifactTypeRequiredError(
        typeof payload.message === 'string' && payload.message.length > 0
          ? payload.message
          : 'artifact_type_required',
        ambiguous,
      );
    }
  }

  if (!response.ok) {
    throw new Error(await parseWpRestMessage(response));
  }

  const data = (await response.json()) as { artifact?: InboxArtifactPublic };
  if (!data.artifact) {
    throw new Error('Réponse de téléversement invalide.');
  }

  return { status: response.status, artifact: data.artifact };
};

export const postInboxPreview = async (
  bootstrapKey: string,
  artifactIds: number[],
): Promise<{ previews: MappingPreviewEntry[] }> => {
  const bootstrap = getBootstrapData(bootstrapKey);
  const response = await fetch(`${bootstrap.restUrl}cabinet/profile/artifacts/inbox/preview`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': bootstrap.nonce,
    },
    body: JSON.stringify({ artifact_ids: artifactIds }),
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    throw new Error(await parseWpRestMessage(response));
  }

  return (await response.json()) as { previews: MappingPreviewEntry[] };
};

export type ConfirmMappingItem = {
  artifact_id: number;
  target_company_id: number;
  confirmed_label: string;
};

export const postConfirmMapping = async (
  bootstrapKey: string,
  items: ConfirmMappingItem[],
): Promise<{ results: ConfirmMappingResult[] }> => {
  const bootstrap = getBootstrapData(bootstrapKey);
  const response = await fetch(`${bootstrap.restUrl}cabinet/profile/artifacts/inbox/confirm-mapping`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': bootstrap.nonce,
    },
    body: JSON.stringify({ items }),
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    throw new Error(await parseWpRestMessage(response));
  }

  return (await response.json()) as { results: ConfirmMappingResult[] };
};

export const createFileExtractionJob = async (
  bootstrapKey: string,
  inboxArtifactIds: number[],
): Promise<{ job_id: number }> => {
  const bootstrap = getBootstrapData(bootstrapKey);
  const response = await fetch(`${bootstrap.restUrl}cabinet/profile/import-jobs/fileextraction`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': bootstrap.nonce,
    },
    body: JSON.stringify({ inbox_artifact_ids: inboxArtifactIds }),
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    throw new Error(await parseWpRestMessage(response));
  }

  const data = (await response.json()) as { job_id?: number };
  if (typeof data.job_id !== 'number' || data.job_id <= 0) {
    throw new Error('Réponse de création de job invalide.');
  }

  return { job_id: data.job_id };
};

export const getFileExtractionJob = async (
  bootstrapKey: string,
  jobId: number,
): Promise<FileExtractionJobPoll> => {
  const bootstrap = getBootstrapData(bootstrapKey);
  const response = await fetch(`${bootstrap.restUrl}cabinet/profile/import-jobs/fileextraction/${jobId}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'X-WP-Nonce': bootstrap.nonce,
    },
  });

  if (response.status === 401) {
    throw new Error('SESSION_EXPIRED');
  }

  if (!response.ok) {
    throw new Error(await parseWpRestMessage(response));
  }

  return (await response.json()) as FileExtractionJobPoll;
};

export const isJobTerminal = (job: FileExtractionJobPoll): boolean => {
  const s = job.status;
  return s === 'completed' || s === 'partial' || s === 'failed' || s === 'cancelled';
};
